import React from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
const HERO_BG_DESKTOP = 'https://cdn.magicpatterns.com/uploads/m4koWPTgG8DAeDjwDgnQuT/ChatGPT_Image_May_26,_2026,_02_24_35_AM.png';
const HERO_BG_MOBILE = 'https://cdn.magicpatterns.com/uploads/xtuGTaqWgoE3wibMfeR8rR/ChatGPT_Image_May_26,_2026,_02_26_12_AM.png';
const ICON_LOWER_PRICE = 'https://cdn.magicpatterns.com/uploads/iDdnjmNHgWMgzXpcUDbmXJ/Mask_group_(13).png';
const ICON_RECOMMENDATIONS = 'https://cdn.magicpatterns.com/uploads/iKCwBaqB4DaB2fFisd2rf5/Mask_group_(14).png';
const ICON_COMMISSION = 'https://cdn.magicpatterns.com/uploads/pg8dMYDz9nDhdnCEEKFYNm/Mask_group_(15).png';
const AVATARS = ['https://i.pravatar.cc/100?img=1', 'https://i.pravatar.cc/100?img=5', 'https://i.pravatar.cc/100?img=8', 'https://i.pravatar.cc/100?img=12'];
const REGISTER_URL = 'https://tokoteta.id/auth/register';
export function HeroSection() {
  const {
    t
  } = useLanguage();
  const featureIcons = [ICON_LOWER_PRICE, ICON_RECOMMENDATIONS, ICON_COMMISSION];
  return <>
      {/* ============ DESKTOP HERO ============ */}
      <section className="hidden md:flex relative w-full h-screen min-h-[640px] max-h-[900px] overflow-hidden bg-black flex-col" data-id="element-176">
        {/* Background image */}
        <div className="absolute inset-0" data-id="element-177">
          <img src={HERO_BG_DESKTOP} alt="" aria-hidden="true" className="w-full h-full object-cover object-center" data-id="element-178" />
          <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0) 78%)'
        }} data-id="element-179" />
        </div>

        {/* Content area */}
        <div className="relative flex-1 flex items-center px-6 lg:px-10 pt-20" data-id="element-180">
          <div className="max-w-7xl w-full mx-auto" data-id="element-181">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="max-w-xl text-white" data-id="element-182">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.05] mb-4 tracking-tight" data-id="element-183">
                {t.hero.title}
              </h1>

              <p className="text-sm lg:text-base text-white/80 mb-6 max-w-md leading-relaxed" data-id="element-184">
                {t.hero.description}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-6" data-id="element-185">
                {t.hero.features.map((feature, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.4,
                delay: 0.2 + index * 0.1
              }} className="flex items-center gap-3" data-id="element-186">
                    <img src={featureIcons[index]} alt="" aria-hidden="true" className="w-10 h-10 lg:w-11 lg:h-11 object-contain shrink-0" data-id="element-187" />
                    <div data-id="element-188">
                      <h3 className="text-sm lg:text-base font-bold text-white leading-tight" data-id="element-189">
                        {feature.title}
                      </h3>
                      <p className="text-xs lg:text-sm text-white/65 leading-snug" data-id="element-190">
                        {feature.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>

              {/* CTA */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.6
            }} data-id="element-191">
                <a href={REGISTER_URL} className="inline-flex items-center justify-center gap-2 bg-[#0288F2] hover:bg-[#0674CA] text-white font-semibold px-6 py-3 rounded-full transition-all shadow-lg hover:shadow-xl text-sm lg:text-base" data-id="element-192">
                  {t.hero.ctaPrimary}
                  <ArrowRight size={18} data-id="element-193" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Trust strip - desktop */}
        <div className="relative z-10 pb-5 px-6 lg:px-10" data-id="element-194">
          <div className="max-w-7xl mx-auto" data-id="element-195">
            <div className="bg-white/95 backdrop-blur-sm rounded-full px-5 py-2.5 flex items-center gap-4 lg:gap-6 shadow-lg flex-wrap" data-id="element-196">
              <div className="flex items-center gap-3" data-id="element-197">
                <div className="flex -space-x-2" data-id="element-198">
                  {AVATARS.slice(0, 3).map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-7 h-7 rounded-full border-2 border-white object-cover" data-id="element-199" />)}
                </div>
                <span className="text-sm font-medium text-gray-700" data-id="element-200">
                  {t.hero.trust.text}
                </span>
              </div>
              <div className="h-5 w-px bg-gray-300 hidden sm:block" data-id="element-201" />
              <div className="flex items-center gap-2" data-id="element-202">
                <ShieldCheck size={18} className="text-[#A855F7]" data-id="element-203" />
                <span className="text-sm font-medium text-gray-700" data-id="element-204">
                  {t.hero.trust.tags}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MOBILE HERO ============ */}
      <section className="md:hidden relative w-full min-h-screen bg-black flex flex-col overflow-hidden" data-id="element-205">
        {/* Background image */}
        <div className="absolute inset-0" data-id="element-206">
          <img src={HERO_BG_MOBILE} alt="" aria-hidden="true" className="w-full h-auto object-contain object-top" data-id="element-207" />

          {/* Dark top overlay for readability */}
          <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 25%, rgba(0,0,0,0.12) 45%, rgba(255,255,255,0) 62%, rgba(255,255,255,1) 100%)"
        }} data-id="element-208" />
        </div>

        {/* ================= HEADER ================= */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative pt-24 px-5 z-10" data-id="element-209">
          {/* TITLE */}
          <h1 className="text-[48px] font-bold text-white leading-[0.95] tracking-tight max-w-[350px] mb-5" data-id="element-210">
            {t.hero.title}
          </h1>

          {/* DESCRIPTION */}
          <p className="text-[15px] text-white leading-relaxed max-w-[300px]" data-id="element-211">
            {t.hero.description}
          </p>
        </motion.div>

        {/* Spacer */}
        <div className="h-[340px]" data-id="element-212" />

        {/* ================= BOTTOM CONTENT ================= */}
        <div className="relative z-10 px-4 pb-10" data-id="element-213">
          {/* Feature Card */}
          <div className="bg-white rounded-[28px] shadow-xl border border-gray-100 overflow-hidden mb-4" data-id="element-214">
            {t.hero.features.map((feature, index) => <div key={index} className={`flex items-start gap-4 px-5 py-4 ${index !== t.hero.features.length - 1 ? "border-b border-gray-100" : ""}`} data-id="element-215">
                {/* ICON */}
                <div className="w-11 h-11 rounded-2xl bg-[#F5F9FF] flex items-center justify-center shrink-0" data-id="element-216">
                  <img src={featureIcons[index]} alt="" aria-hidden="true" className="w-6 h-6 object-contain" data-id="element-217" />
                </div>

                {/* TEXT */}
                <div className="flex-1 min-w-0" data-id="element-218">
                  <h3 className="text-[15px] font-semibold text-gray-900 leading-tight mb-1" data-id="element-219">
                    {feature.title}
                  </h3>

                  <p className="text-[13px] text-gray-500 leading-relaxed" data-id="element-220">
                    {feature.desc}
                  </p>
                </div>
              </div>)}
          </div>

          {/* CTA */}
          <a href={REGISTER_URL} className="flex items-center justify-center gap-2 bg-[#0288F2] hover:bg-[#0674CA] active:scale-[0.98] text-white font-semibold py-4 rounded-full transition-all shadow-lg text-[17px] mb-4" data-id="element-221">
            {t.hero.ctaPrimary}
            <ArrowRight size={18} data-id="element-222" />
          </a>

          {/* TRUST STRIP */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 border border-white/50 shadow-sm" data-id="element-223">
            {/* TOP */}
            <div className="flex items-center gap-3 mb-2" data-id="element-224">
              {/* AVATARS */}
              <div className="flex -space-x-2 shrink-0" data-id="element-225">
                {AVATARS.map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-7 h-7 rounded-full border-2 border-white object-cover" data-id="element-226" />)}
              </div>

              {/* TEXT */}
              <span className="text-[14px] font-medium text-gray-700 leading-snug" data-id="element-227">
                {t.hero.trust.text}
              </span>
            </div>

            {/* BOTTOM */}
            <div className="flex items-center gap-2 border-t border-gray-200 pt-2" data-id="element-228">
              <ShieldCheck size={14} className="text-[#A855F7] shrink-0" data-id="element-229" />

              <span className="text-[12px] font-medium text-gray-600" data-id="element-230">
                {t.hero.trust.tags}
              </span>
            </div>
          </div>
        </div>
      </section>
    </>;
}